import { useEffect, useState } from "react";
import { XIcon } from "lucide-react";
import type { PayrollUiLeaveInfo } from "../../types";

type LeaveManagementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  employeeName: string;
  currentLeaveInfo?: PayrollUiLeaveInfo;
  onSave?: (input: PayrollUiLeaveInfo) => void | Promise<void>;
};

const LEAVE_TYPES = [
  "Maternity",
  "Paternity",
  "Medical",
  "Personal",
  "Sabbatical",
] as const;

const today = () => new Date().toISOString().split("T")[0] ?? "";

export function LeaveManagementModal({
  isOpen,
  onClose,
  employeeName,
  currentLeaveInfo,
  onSave,
}: LeaveManagementModalProps) {
  const [leaveType, setLeaveType] =
    useState<PayrollUiLeaveInfo["leaveType"]>("Medical");
  const [startDate, setStartDate] = useState(today());
  const [expectedReturnDate, setExpectedReturnDate] = useState("");
  const [partialPayPercentage, setPartialPayPercentage] = useState("0");

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setLeaveType(currentLeaveInfo?.leaveType ?? "Medical");
    setStartDate(currentLeaveInfo?.startDate ?? today());
    setExpectedReturnDate(currentLeaveInfo?.expectedReturnDate ?? "");
    setPartialPayPercentage(
      currentLeaveInfo?.partialPayPercentage == null
        ? "0"
        : String(currentLeaveInfo.partialPayPercentage),
    );
  }, [currentLeaveInfo, isOpen]);

  if (!isOpen) {
    return null;
  }

  const isValid =
    startDate !== "" &&
    expectedReturnDate !== "" &&
    partialPayPercentage !== "" &&
    Number(partialPayPercentage) >= 0 &&
    Number(partialPayPercentage) <= 100;

  const handleSave = async () => {
    if (!isValid) {
      return;
    }
    await onSave?.({
      leaveType,
      startDate,
      expectedReturnDate,
      partialPayPercentage: Number(partialPayPercentage),
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/20 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative flex max-h-[90vh] w-full max-w-[600px] flex-col overflow-hidden rounded-[12px] bg-surface-light shadow-xl animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 p-5">
          <div>
            <h2 className="font-heading text-lg font-semibold text-iron-grey">
              Update Leave Status
            </h2>
            <p className="mt-0.5 font-body text-sm text-text-secondary">
              For {employeeName}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary transition-colors hover:text-iron-grey"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 overflow-y-auto p-6">
          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Leave Type
            </label>
            <select
              value={leaveType}
              onChange={(event) =>
                setLeaveType(event.target.value as PayrollUiLeaveInfo["leaveType"])
              }
              className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              {LEAVE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div>
              <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
                Expected Return Date
              </label>
              <input
                type="date"
                value={expectedReturnDate}
                onChange={(event) => setExpectedReturnDate(event.target.value)}
                className="w-full rounded-[6px] border border-border bg-surface-light px-3 py-2 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block font-body text-sm font-medium text-iron-grey">
              Partial Pay Percentage
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={partialPayPercentage}
                onChange={(event) => setPartialPayPercentage(event.target.value)}
                placeholder="0"
                className="w-full rounded-[6px] border border-border bg-surface-light py-2 pl-3 pr-8 font-body text-sm text-iron-grey focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 font-body text-sm text-text-secondary">
                %
              </span>
            </div>
            <p className="mt-1 font-body text-xs text-text-secondary">
              Enter 0 for unpaid leave, 100 for full pay
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-border bg-background-light p-5">
          <button
            onClick={onClose}
            className="rounded-[6px] border border-border bg-surface-light px-4 py-2 font-body text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
          >
            Cancel
          </button>
          <button
            disabled={!isValid}
            onClick={() => void handleSave()}
            className="rounded-[6px] bg-primary px-4 py-2 font-body text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Update Leave
          </button>
        </div>
      </div>
    </div>
  );
}
